import kotlinx.browser.window
import features.auth.AuthFeature
import features.auth.validation.initUserValidation
import features.auth.validation.validateUserEmail
import core.firebase.FirebaseService
import features.teams.handlers.openTeam
import features.shifts.handlers.selectShiftColorFromPicker
import features.shifts.handlers.selectEditShiftColorFromPicker
import features.members.handlers.*
import features.shifts.handlers.*
import features.schedule.handlers.*
import features.attendance.handlers.*
import features.overtime.handlers.*
import features.timeoff.handlers.*
import features.auth.handlers.*
import features.notifications.handlers.*


fun main() {
    // Expose functions to window IMMEDIATELY (before onload)
    console.log("🔧 Exposing functions to window...")
    
    // Team handlers
    window.asDynamic().openTeam = ::openTeam
    
    // Shift handlers
    window.asDynamic().selectShiftColorFromPicker = ::selectShiftColorFromPicker
    window.asDynamic().selectEditShiftColorFromPicker = ::selectEditShiftColorFromPicker
    window.asDynamic().createShift = ::createShift
    window.asDynamic().updateShift = ::updateShift
    
    // Member handlers
    window.asDynamic().addMember = ::addMember
    window.asDynamic().saveMemberChanges = ::saveMemberChanges
    window.asDynamic().removeMember = ::removeMember
    window.asDynamic().showAddMemberModal = ::showAddMemberModal
    window.asDynamic().openEditMember = ::openEditMember
    window.asDynamic().openMemberSchedule = ::openMemberSchedule
    window.asDynamic().handleFileImport = ::handleFileImport
    window.asDynamic().exportMembers = ::exportMembers
    
    // Validation handlers
    window.asDynamic().validateUserEmail = ::validateUserEmail
    
    // Schedule handlers
    window.asDynamic().switchScheduleView = ::switchScheduleView
    
    // Attendance handlers
    window.asDynamic().submitClockIn = ::submitClockIn
    window.asDynamic().submitClockOut = ::submitClockOut
    window.asDynamic().getAttendanceStatusForModal = { userId: String, teamId: String ->
        features.attendance.helpers.AttendanceHelpers.getAttendanceStatus(userId, teamId)
    }
    
    // Overtime handlers
    window.asDynamic().submitOvertime = ::submitOvertime
    window.asDynamic().updateOvertimeHours = ::updateOvertimeHours
    
    // Time off handlers
    window.asDynamic().submitTimeOffRequest = ::submitTimeOffRequest
    window.asDynamic().approveTimeOffRequest = ::approveTimeOffRequest
    window.asDynamic().rejectTimeOffRequest = ::rejectTimeOffRequest
    window.asDynamic().updateTimeOffDays = { updateTimeOffDays() }
    window.asDynamic().addLeaveCategory = ::addLeaveCategory
    window.asDynamic().deleteLeaveCategory = ::deleteLeaveCategory
    window.asDynamic().createLeaveType = ::createLeaveType
    window.asDynamic().exportTimeOffToCSV = ::exportTimeOffToCSV
    
    // Schedule export handlers
    window.asDynamic().exportShiftScheduleToExcel = { features.schedule.handlers.exportShiftScheduleToExcel("Team Name", emptyList()) }
    
    // Debug export handlers (console.log only, no CSV export)
    window.asDynamic().debugAttendanceData = ::debugAttendanceData
    window.asDynamic().debugOvertimeData = ::debugOvertimeData
    
    // Test dummy export
    window.asDynamic().testDummyExport = ::testDummyExport
    
    // Auth handlers
    window.asDynamic().authFeatureLogin = { AuthFeature.login() }
    window.asDynamic().devLogin = ::handleDevLogin
    
    // Notification handlers
    window.asDynamic().markNotificationAsRead = ::markNotificationAsRead
    window.asDynamic().markAllNotificationsAsRead = ::markAllNotificationsAsRead
    
    // Expose AppState and ModalManager to window for onclick handlers
    window.asDynamic().AppState = AppState
    window.asDynamic().ModalManager = js("({})")
    window.asDynamic().ModalManager.show = { modalId: String -> shared.ui.ModalManager.show(modalId) }
    
    console.log("✅ All functions exposed to window")
    console.log("  - openTeam:", window.asDynamic().openTeam)
    console.log("  - addMember:", window.asDynamic().addMember)
    console.log("  - validateUserEmail:", window.asDynamic().validateUserEmail)
    console.log("  - AppState:", window.asDynamic().AppState)
    console.log("  - ModalManager.show:", window.asDynamic().ModalManager.show)
    
    window.onload = {
        console.log("=" .repeat(80))
        console.log("🚀 TeamMate App - Kotlin/JS Loaded!")
        console.log("=" .repeat(80))
        
        // Initialize mock data
        console.log("📦 Step 1: Initializing mock data...")
        AppState.initMockData()
        console.log("✅ Mock data initialized. Teams count:", AppState.teams.size)
        AppState.teams.forEach { team ->
            console.log("  - Team: ${team.name}, ID: ${team.id}, Slug: ${team.slug}")
        }
        
        // Parse URL to restore state
        console.log("\n🔍 Step 2: Parsing URL to restore state...")
        val path = kotlinx.browser.window.location.pathname
        val hash = kotlinx.browser.window.location.hash
        
        console.log("📍 Current URL path:", path)
        console.log("📍 Current URL hash:", hash)
        console.log("📍 Full URL:", kotlinx.browser.window.location.href)
        
        // Check if URL contains team slug (e.g., /team/solo-office)
        console.log("\n🔎 Step 3: Checking if URL starts with '/team/'...")
        if (path.startsWith("/team/")) {
            console.log("✅ URL starts with '/team/' - attempting to restore team state")
            val teamSlug = path.substring(6) // Remove "/team/" prefix
            console.log("🏷️ Extracted team slug from URL:", teamSlug)
            
            // Find team by slug in mock data
            console.log("🔍 Searching for team with slug:", teamSlug)
            val team = AppState.teams.find { it.slug == teamSlug }
            
            if (team != null) {
                console.log("✅ Team found!")
                console.log("  - Team ID:", team.id)
                console.log("  - Team Name:", team.name)
                console.log("  - Team Slug:", team.slug)
                
                AppState.currentTeam = team
                AppState.currentPage = "team-detail"
                console.log("✅ AppState.currentTeam set to:", team.name)
                console.log("✅ AppState.currentPage set to:", AppState.currentPage)
                
                // Restore tab from hash if present
                console.log("\n🔎 Step 4: Checking for tab in URL hash...")
                if (hash.isNotEmpty() && hash.startsWith("#")) {
                    val tabFromUrl = hash.substring(1)
                    console.log("🏷️ Extracted tab from hash:", tabFromUrl)
                    
                    val validTabs = listOf("members", "shifts", "schedule", "attendance", "overtime", "timeoff", "settings")
                    console.log("📋 Valid tabs:", validTabs.joinToString(", "))
                    
                    if (tabFromUrl in validTabs) {
                        AppState.currentTab = tabFromUrl
                        console.log("✅ AppState.currentTab set to:", tabFromUrl)
                    } else {
                        console.log("⚠️ Tab '$tabFromUrl' is not valid, keeping default:", AppState.currentTab)
                    }
                } else {
                    console.log("ℹ️ No hash in URL, using default tab:", AppState.currentTab)
                }
                
                console.log("\n📊 Final restored state:")
                console.log("  - currentTeam:", AppState.currentTeam?.name)
                console.log("  - currentPage:", AppState.currentPage)
                console.log("  - currentTab:", AppState.currentTab)
            } else {
                console.log("❌ Team NOT found with slug:", teamSlug)
                console.log("Available team slugs:", AppState.teams.map { it.slug }.joinToString(", "))
            }
        } else {
            console.log("ℹ️ URL does not start with '/team/' - no state to restore")
            console.log("Current AppState.currentPage:", AppState.currentPage)
        }
        
        console.log("\n" + "=".repeat(80))
        
        // Save URL state for restoration after auth
        val savedPath = kotlinx.browser.window.location.pathname
        val savedHash = kotlinx.browser.window.location.hash
        
        // Listen for auth state changes using new AuthFeature
        AuthFeature.onAuthStateChanged { user ->
            console.log("🔐 Auth state changed. User:", user?.name ?: "null")
            
            if (user != null) {
                console.log("✅ User is logged in")
                AppState.currentUser = user
                
                // Save user to Firestore users collection
                console.log("💾 Saving user to Firestore...")
                features.auth.data.UserRepository.createOrUpdateUser(user)
                    .then {
                        console.log("✅ User saved to Firestore")
                        AppState.currentUser = user
                        
                        // Always load teams from Firebase to ensure we have latest data and no mock data
                        console.log("📥 Loading user teams from Firebase...")
                        features.teams.data.TeamRepository.loadUserTeams(user.id)
                            .then { teams ->
                                console.log("✅ Teams loaded from Firebase:", teams.size)
                                // Log team names and IDs for debugging duplicates
                                teams.forEach { t -> console.log("   - ${t.name} (${t.id})") }
                                
                                AppState.teams.clear()
                                AppState.teams.addAll(teams)
                                AppState.teamsLoaded = true
                                
                                // Setup notification listener for real-time updates
                                console.log("🔔 Setting up notification listener...")
                                setupNotificationListener()
                        
                                // Check if we need to restore URL state
                                if (savedPath.startsWith("/team/")) {
                                    console.log("📍 URL state detected, restoring team context...")
                                    
                                    // Now try to restore team from URL
                                    val teamSlug = savedPath.substring(6) // Remove "/team/" prefix
                                    console.log("🔍 Restoring team with slug:", teamSlug)
                                    val team = AppState.teams.find { it.slug == teamSlug }
                                    
                                    if (team != null) {
                                        console.log("✅ Team found! Restoring state for:", team.name)
                                        
                                        // Set current team
                                        AppState.currentTeam = team
                                        
                                        // Determine which tab to restore
                                        var tabToRestore = "members" // default
                                        if (savedHash.isNotEmpty() && savedHash.startsWith("#")) {
                                            val tabFromUrl = savedHash.substring(1)
                                            val validTabs = listOf("members", "shifts", "schedule", "attendance", "overtime", "timeoff", "settings")
                                            if (tabFromUrl in validTabs) {
                                                tabToRestore = tabFromUrl
                                                console.log("📍 Restoring tab:", tabToRestore)
                                            }
                                        }
                                        
                                        // Set tab BEFORE navigating to avoid flicker
                                        AppState.currentTab = tabToRestore
                                        
                                        // Navigate to team-detail page (will show loading state)
                                        AppState.isLoading = true
                                        AppState.currentPage = "team-detail"
                                        Router.render()
                                        
                                        // Load team data from Firestore
                                        console.log("📥 Loading team data for:", team.id)
                                        
                                        // Initialize mock data for demo purposes
                                        AppState.initMockDataForTeam(team.id)
                                        
                                        // Load members (always needed for team page)
                                        features.members.data.MemberRepository.loadMembers(team.id)
                                            .then { members ->
                                                console.log("✅ Members loaded:", members.size)
                                                AppState.members[team.id] = members.toMutableList()
                                                
                                                // Load shifts (needed for multiple tabs)
                                                features.shifts.data.ShiftRepository.loadShifts(team.id)
                                                    .then { shifts ->
                                                        console.log("✅ Shifts loaded:", shifts.size)
                                                        AppState.shiftTemplates[team.id] = shifts.toMutableList()
                                                        
                                                        // Load schedule if on schedule tab
                                                        if (tabToRestore == "schedule") {
                                                            val now = kotlin.js.Date()
                                                            val year = now.getFullYear().toInt()
                                                            val month = (now.getMonth() + 1).toInt()
                                                            
                                                            console.log("📅 Loading schedule for $year-$month...")
                                                            features.schedule.data.ScheduleRepository.loadSchedule(team.id, year, month)
                                                                .then { schedule ->
                                                                    if (schedule != null) {
                                                                        AppState.schedules[team.id] = schedule
                                                                        console.log("✅ Schedule loaded")
                                                                    }
                                                                    AppState.isLoading = false
                                                                    console.log("🎨 Rendering with restored state...")
                                                                    Router.render()
                                                                }
                                                                .catch { error ->
                                                                    console.error("❌ Error loading schedule:", error)
                                                                    AppState.isLoading = false
                                                                    Router.render()
                                                                }
                                                        } else {
                                                            // For other tabs, just render
                                                            AppState.isLoading = false
                                                            console.log("🎨 Rendering with restored state...")
                                                            Router.render()
                                                        }
                                                    }
                                                    .catch { error ->
                                                        console.error("❌ Error loading shifts:", error)
                                                        AppState.isLoading = false
                                                        Router.render()
                                                    }
                                            }
                                            .catch { error ->
                                                console.error("❌ Error loading members:", error)
                                                AppState.isLoading = false
                                                Router.render()
                                            }
                                    } else {
                                        console.log("❌ Team not found, navigating to dashboard")
                                        Router.navigateTo("dashboard")
                                    }
                                } else if (AppState.currentPage == "login") {
                                    console.log("📍 No URL state, navigating to home...")
                                    Router.navigateTo("home")
                                } else {
                                    console.log("📍 URL state restored, rendering current page:", AppState.currentPage)
                                    // Just re-render current page
                                    Router.render()
                                }
                            }
                            .catch { error ->
                                console.error("❌ Error loading teams:", error)
                                // Still try to navigate
                                Router.navigateTo("home")
                            }
                    }
                    .catch { error ->
                        console.error("❌ Error saving user:", error)
                        // Continue anyway - show current page or dashboard
                        if (AppState.currentPage == "login") {
                            Router.navigateTo("dashboard")
                        } else {
                            Router.render()
                        }
                    }
            } else {
                console.log("❌ User is not logged in")
                AppState.currentUser = null
                Router.navigateTo("login")
            }
        }
    }
    
    // Inject photo preview modal script
    val photoPreviewScript = kotlinx.browser.document.createElement("script") as org.w3c.dom.HTMLScriptElement
    photoPreviewScript.innerHTML = """
        window.showPhotoPreview = function(checkInPhoto, checkOutPhoto, date, memberName) {
            // Create modal if doesn't exist
            let modal = document.getElementById('photo-preview-modal');
            if (!modal) {
                modal = document.createElement('div');
                modal.id = 'photo-preview-modal';
                modal.className = 'fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] hidden flex items-center justify-center p-4';
                modal.onclick = function(e) {
                    if (e.target === modal) {
                        modal.classList.add('hidden');
                    }
                };
                document.body.appendChild(modal);
            }
            
            // Build modal content
            let content = '<div class="bg-dark-800 rounded-2xl border border-dark-700 max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto" onclick="event.stopPropagation()">';
            content += '<div class="flex justify-between items-center mb-4">';
            content += '<div><h3 class="text-xl font-bold">' + memberName + '</h3>';
            content += '<p class="text-slate-400 text-sm">' + date + '</p></div>';
            content += '<button onclick="document.getElementById(\'photo-preview-modal\').classList.add(\'hidden\')" class="text-slate-400 hover:text-white text-2xl">✕</button>';
            content += '</div>';
            
            // Photos grid
            content += '<div class="grid grid-cols-1 md:grid-cols-2 gap-4">';
            
            // Check In Photo
            if (checkInPhoto) {
                content += '<div>';
                content += '<p class="text-sm text-slate-400 mb-2">Clock In Photo</p>';
                content += '<img src="' + checkInPhoto + '" class="w-full rounded-xl border border-dark-700 cursor-zoom-in" onclick="window.open(\'' + checkInPhoto + '\', \'_blank\')" />';
                content += '</div>';
            }
            
            // Check Out Photo
            if (checkOutPhoto) {
                content += '<div>';
                content += '<p class="text-sm text-slate-400 mb-2">Clock Out Photo</p>';
                content += '<img src="' + checkOutPhoto + '" class="w-full rounded-xl border border-dark-700 cursor-zoom-in" onclick="window.open(\'' + checkOutPhoto + '\', \'_blank\')" />';
                content += '</div>';
            }
            
            content += '</div></div>';
            
            modal.innerHTML = content;
            modal.classList.remove('hidden');
        };
        
        console.log('✅ Photo preview function loaded');
    """
    kotlinx.browser.document.body?.appendChild(photoPreviewScript)
}
